// TimelineFrame — chrome shared by Timeline (TTS) and STSMasterTimeline:
// controls row + minimap + scrollWrap + track skeleton + ruler/ticks +
// overflow shade + end marker + playhead.
//
// Design: the frame is "dumb" / presentational. All hooks
// (useTimelineZoom, usePlayheadDrag), refs (scrollWrapRef, trackRef),
// measurement state (wrapW, scrollLeft) and the playback controller
// registration LIVE IN THE FLAVOUR — because chip drag handlers need
// direct, stable access to those values inside their useCallback closures.
// Lifting them into the frame would force every chip handler to thread
// the values back out via a render-prop API and break stable identities.
//
// The frame:
//   - reads everything it needs via props (zoom getters, playhead state,
//     chrome event handlers, refs)
//   - renders the controls row, minimap, scrollWrap, track wrapper, ticks,
//     ruler, overflow shade, end marker and playhead
//   - mounts an internal ResizeObserver on the passed-in scrollWrapRef and
//     a scroll listener that mirrors scrollLeft into a setter the flavour
//     supplies (so the same ref is used by useTimelineZoom in the flavour)
//   - takes chips / ghosts / drag-popup as `children`, rendered as direct
//     children of the .track div between the ticks/labels and the playhead
//
// Constraints (kept identical to pre-refactor STSMasterTimeline +
// post-Phase-A Timeline):
//   - Chips MUST be direct children of the track div (pointer-capture +
//     z-index ordering depend on it). `children` is rendered straight into
//     the track wrapper.
//   - Playhead MUST be the LAST child of the track. Frame renders it after
//     `children`.
//   - handleDragActiveRef is a MutableRef shared with TimelineMinimap so
//     the scroll listener gate stays consistent with the gesture timing.
//   - No useLayoutEffect — measurement is via ResizeObserver only.
//   - The ResizeObserver + scroll listener live HERE so the flavour
//     doesn't have to duplicate them, but they observe the ref that the
//     flavour created and passes in.

import { useEffect, useMemo, useRef, type CSSProperties, type ReactNode, type RefObject, type MutableRefObject } from 'react';
import { TimelineMinimap, type MinimapChip } from './TimelineMinimap';
import { setTimelinePlaybackController } from '@/lib/timelinePlayback';
import { useTimelineWheel } from '@/hooks/useTimelineWheel';
import styles from './TimelineFrame.module.css';

// Manual zoom-in cap — mirrors PX_PER_SEC_MAX in useTimelineZoom and is
// passed to the minimap so the handle drag clamps to the same range.
export const PX_PER_SEC_MAX = 500;

const TICK_STEP = 1;

function fmtTime(sec: number): string {
  const s = Math.max(0, sec);
  const mm = Math.floor(s / 60);
  const ss = (s - mm * 60).toFixed(1);
  return `${mm}:${ss.padStart(4, '0')}`;
}

export interface TimelineFrameProps {
  // ── Refs the flavour created (so its hooks share the same elements) ──
  scrollWrapRef: RefObject<HTMLDivElement | null>;
  trackRef: RefObject<HTMLDivElement | null>;

  // ── Measurement state — frame OBSERVES the scrollWrap and writes back ─
  /** Frame writes width via a setter the flavour owns so useTimelineZoom
   *  can read the same value. */
  setWrapW: (w: number) => void;
  /** Mirror of scrollLeft — frame writes via setter the flavour owns so
   *  the minimap viewport rect stays in sync. Suppressed during minimap-
   *  handle drags via handleDragActiveRef. */
  setScrollLeft: (n: number) => void;
  scrollLeft: number;
  wrapW: number;
  /** Shared with TimelineMinimap so a passive scroll event during a
   *  minimap-handle drag doesn't fight the drag's own scrollLeft writes. */
  handleDragActiveRef: MutableRefObject<boolean>;

  // ── Domain inputs ────────────────────────────────────────────────────
  totalDur: number;
  /** "Official" asset length used for the end marker + overflow shade.
   *  Pass 0 to suppress both. */
  boundary: number;
  inOvertime: boolean;
  /** simpleMode (Multi-Lingual, Task 9): suppresses ONLY the overflow
   *  shade — the end marker stays visible as a plain limit line. Default
   *  false so standard projects render byte-identical. */
  hideOverflowShade?: boolean;
  /** Flex-Timeline (Multi-Lingual): Admin-Richtwert als gestrichelte
   *  Limit-Linie mit Label. Null/undefined = keine Linie. */
  limitSec?: number | null;

  // ── Zoom (flavour owns useTimelineZoom and forwards values + setters) ─
  pxPerSec: number;
  fitPxPerSec: number;
  trackWidth: number;
  pxPerSecManual: number;
  stickyFit: boolean;
  setPxPerSecManual: (n: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomFit: () => void;

  // ── Playback hookups (flavour owns the preview hook) ──────────────────
  playheadSec: number;
  /** displayPlayheadSec === playheadDrag ?? playheadSec — flavour computes
   *  this so the drag ghost stays in sync with the hook the flavour owns. */
  displayPlayheadSec: number;
  isPlaying: boolean;
  isPlayheadDragging: boolean;
  onPlayheadPointerDown: (e: React.PointerEvent<Element>) => void;
  /** Used by toggle-play. */
  play: (at?: number) => void | Promise<void>;
  stop: () => void;
  /** Ruler click → seek; back-to-start → seek(0). */
  seek: (sec: number) => void;
  /** Resets playhead to 0 and starts fresh — wired to the playback
   *  controller singleton so VideoPlayer's click overlay routes through
   *  the unified pipeline. */
  playFromStart: () => void;

  // ── Controls customization ────────────────────────────────────────────
  mixerSlot?: ReactNode;
  /** Label for the fit-content button — "Fit Blocks" (TTS) or "Fit
   *  Clips" (STS). */
  fitContentLabel: string;
  fitContentTitle: string;
  fitContentAriaLabel: string;
  onFitContent: () => void;
  autoZoom: boolean;
  onToggleAutoZoom: () => void;

  // ── Minimap data ──────────────────────────────────────────────────────
  minimapChips: MinimapChip[];
  /** Multi-Voice (#173) minimap stacking: active voiceIds in display order.
   *  When this has ≥2 entries the minimap splits into per-voice mini-rows.
   *  STS + single-voice TTS omit this and get the legacy flat layout. */
  minimapActiveVoiceIds?: string[];
  /** Asset's full voiceIds list — needed by the minimap to compute the
   *  shade-color for each track in multi-voice mode. */
  minimapAssetVoiceIds?: string[];

  // ── Per-flavour optional behaviour ────────────────────────────────────
  /** When provided, ruler-click is suppressed if the guard returns true.
   *  STS uses this to bail when a chip drag is in progress. */
  onRulerClickGuard?: () => boolean;
  /** Slot rendered AFTER the scrollWrap — used by STS for the right-click
   *  context menu (positioned via inline style, frame doesn't position). */
  overlaySlot?: ReactNode;
  /** Extra className applied to the root in addition to the frame's base.
   *  Use sparingly — most cross-flavour modifiers should go through props
   *  (e.g. isDragging) so the frame controls the cascade. */
  rootClassName?: string;
  /** When true, applies the frame's `.isDragging` modifier to the root.
   *  Currently the modifier disables pointer-events on neighbouring
   *  <video> elements so the cursor can roam over the video player
   *  without it swallowing a chip drag. (TTS-relevant; harmless for STS.) */
  isDragging?: boolean;

  // ── Track contents — chips, ghosts, drag popup ────────────────────────
  /** Rendered as direct children of the .track div, BETWEEN the ticks/
   *  labels and the playhead. MUST be a flat list — no extra wrapper —
   *  so chip pointer-capture and z-index match the legacy behaviour.
   *
   *  In multi-track mode (see `multiTracks`), the per-track chip stacks
   *  are passed via `multiTracks[i].trackChildren` instead. `children`
   *  in that mode is rendered as an overlay layer SPANNING all tracks
   *  (used for the drag-popup which sits above the entire stack). */
  children: ReactNode;

  // ── Multi-track mode (Multi-Voice TTS, #173) ──────────────────────────
  /** When provided AND length >= 2, the frame renders N stacked voice-
   *  tracks instead of a single track row. Each track gets its own chip
   *  layer; the trackNumberPane on the left (sticky during horizontal
   *  scroll) labels each row. When omitted or length < 2, the frame
   *  falls back to its single-track layout so STS + single-voice TTS
   *  render bit-for-bit identical to before. */
  multiTracks?: FrameTrack[];
}

/** Per-track render data for multi-track mode. */
export interface FrameTrack {
  /** Stable key — usually the voiceId. */
  key: string;
  /** Number rendered in the trackNumberPane — "1" / "2" / "3" / "?". */
  numberLabel: string;
  /** Inline color for the number text — typically voiceColorVar(voiceId). */
  numberColor: string;
  /** Tooltip for the number — voice display-name. */
  numberTitle: string;
  /** Chips + ghosts for this voice. Rendered as direct children of the
   *  per-voice track row so pointer-capture + z-index work like the
   *  legacy single-track flow. */
  trackChildren: ReactNode;
}

export function TimelineFrame({
  scrollWrapRef,
  trackRef,
  setWrapW,
  setScrollLeft,
  scrollLeft,
  wrapW,
  handleDragActiveRef,
  totalDur,
  boundary,
  inOvertime,
  hideOverflowShade,
  limitSec,
  pxPerSec,
  fitPxPerSec,
  trackWidth,
  pxPerSecManual,
  stickyFit,
  setPxPerSecManual,
  zoomIn,
  zoomOut,
  zoomFit,
  playheadSec,
  displayPlayheadSec,
  isPlaying,
  isPlayheadDragging,
  onPlayheadPointerDown,
  play,
  stop,
  seek,
  playFromStart,
  mixerSlot,
  fitContentLabel,
  fitContentTitle,
  fitContentAriaLabel,
  onFitContent,
  autoZoom,
  onToggleAutoZoom,
  minimapChips,
  minimapActiveVoiceIds,
  minimapAssetVoiceIds,
  onRulerClickGuard,
  overlaySlot,
  rootClassName,
  isDragging,
  children,
  multiTracks,
}: TimelineFrameProps) {
  // Width measurement — observe the scrollWrap the FLAVOUR created so
  // useTimelineZoom (in the flavour) reads the same element. Same lifecycle
  // as the legacy effect.
  //
  // Multi-Voice (#173): when ≥2 tracks render, a 24px sticky-left
  // .trackNumberPane sits inside the scrollWrap alongside the trackArea.
  // The fit math in useTimelineZoom needs the TRACKAREA's available width,
  // not the scrollWrap's full width — otherwise pxPerSec*totalDur fills
  // wrapW exactly, trackStack (= pane + trackArea) ends up wrapW+24,
  // overflows scrollWrap by 24px → scrollbar + dead-space-right. Subtract
  // the pane width here so wrapW always represents trackArea-available.
  const isMultiTrack = (multiTracks?.length ?? 0) >= 2;
  useEffect(() => {
    const el = scrollWrapRef.current;
    if (!el) return;
    const paneW = isMultiTrack ? 24 : 0;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWrapW(Math.max(0, w - paneW));
    });
    ro.observe(el);
    setWrapW(Math.max(0, el.clientWidth - paneW));
    return () => ro.disconnect();
  }, [scrollWrapRef, setWrapW, isMultiTrack]);

  useEffect(() => {
    const el = scrollWrapRef.current;
    if (!el) return;
    const onScroll = () => {
      if (handleDragActiveRef.current) return;
      setScrollLeft(el.scrollLeft);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollWrapRef, handleDragActiveRef, setScrollLeft]);

  // Cmd/Ctrl + wheel = cursor-anchored zoom; Shift + wheel = horizontal pan.
  // Plain wheel passes through so the page scrolls normally — no scroll-trap
  // when the cursor happens to drift over the timeline mid-page-scroll.
  useTimelineWheel({
    scrollWrapRef,
    pxPerSec,
    fitPxPerSec,
    pxPerSecMax: PX_PER_SEC_MAX,
    setPxPerSecManual,
  });

  // Mirror flavour's preview onto the module singleton so VideoPlayer's
  // click overlay routes through the unified pipeline. isPlaying read via
  // ref so the singleton always sees the latest value.
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  useEffect(() => {
    setTimelinePlaybackController({
      play,
      playFromStart,
      stop,
      isPlaying: () => isPlayingRef.current,
    });
    return () => {
      setTimelinePlaybackController(null);
    };
  }, [play, playFromStart, stop]);

  // Adaptive ruler tick / label step — same logic as legacy Timeline.tsx
  // and STSMasterTimeline.tsx.
  const { subTicks, labels } = useMemo(() => {
    const subTicksOut: number[] = [];
    const labelsOut: number[] = [];
    if (!totalDur) return { subTicks: subTicksOut, labels: labelsOut };
    for (let t = 0; t <= totalDur + 0.001; t += TICK_STEP) {
      subTicksOut.push(Math.round(t * 10) / 10);
    }
    const MIN_LABEL_PX = 60;
    const niceSteps = [1, 2, 5, 10, 30, 60, 120, 300, 600];
    let labelStep = niceSteps[niceSteps.length - 1];
    for (const s of niceSteps) {
      if (s * pxPerSec >= MIN_LABEL_PX) {
        labelStep = s;
        break;
      }
    }
    for (let t = 0; t <= totalDur + 0.001; t += labelStep) {
      labelsOut.push(Math.round(t * 10) / 10);
    }
    return { subTicks: subTicksOut, labels: labelsOut };
  }, [totalDur, pxPerSec]);

  const canZoomIn = pxPerSec < PX_PER_SEC_MAX - 0.01;
  const canZoomOut = !stickyFit && pxPerSecManual > fitPxPerSec + 0.01;

  const togglePlay = () => {
    if (isPlaying) stop();
    else void play(playheadSec);
  };

  const handleBackToStart = () => {
    seek(0);
  };

  const handleRulerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onRulerClickGuard?.()) return;
    const el = trackRef.current;
    if (!el || !totalDur || pxPerSec <= 0) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const sec = Math.max(0, Math.min(totalDur, x / pxPerSec));
    seek(Math.round(sec * 10) / 10);
  };

  const wrapCls = [
    styles.scrollWrap,
    pxPerSec * totalDur <= (wrapW || 0) + 1 ? styles.noScrollbar : '',
  ]
    .filter(Boolean)
    .join(' ');
  const rootCls = [
    styles.root,
    isDragging ? styles.isDragging : '',
    rootClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootCls}>
      <div className={styles.controls}>
        <button
          type="button"
          className={styles.ctrlBtn}
          onClick={togglePlay}
          title={isPlaying ? 'Stop' : 'Play'}
          aria-label={isPlaying ? 'Stop' : 'Play'}
        >
          <i className={isPlaying ? 'ph-fill ph-stop' : 'ph-fill ph-play'} />
        </button>

        <button
          type="button"
          className={styles.ctrlBtn}
          onClick={handleBackToStart}
          title="Back to start"
          aria-label="Back to start"
        >
          <i className="ph-bold ph-skip-back" />
        </button>

        {/* Zoom in/out as a tight − / + pair so users read them as one
            stepper control. Fit-to-view sits separately on the right. */}
        <div className={styles.zoomPair}>
          <button
            type="button"
            className={[styles.ctrlBtn, styles.zoomPairBtn].join(' ')}
            onClick={zoomOut}
            disabled={!canZoomOut}
            title="Zoom out"
            aria-label="Zoom out"
          >
            <i className="ph-bold ph-minus" />
          </button>
          <button
            type="button"
            className={[styles.ctrlBtn, styles.zoomPairBtn].join(' ')}
            onClick={zoomIn}
            disabled={!canZoomIn}
            title="Zoom in"
            aria-label="Zoom in"
          >
            <i className="ph-bold ph-plus" />
          </button>
        </div>

        <button
          type="button"
          className={[
            styles.ctrlBtn,
            styles.ctrlBtnLabeled,
            autoZoom ? styles.ctrlBtnActive : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={onToggleAutoZoom}
          title="Auto-zoom on chip click — focus the clicked clip"
          aria-label="Auto-zoom on chip click"
          aria-pressed={autoZoom}
        >
          <i className="ph-bold ph-frame-corners" />
          <span>Auto Zoom</span>
        </button>

        <button
          type="button"
          className={[styles.ctrlBtn, styles.ctrlBtnLabeled].join(' ')}
          onClick={onFitContent}
          title={fitContentTitle}
          aria-label={fitContentAriaLabel}
        >
          <i className="ph-bold ph-arrows-in-simple" />
          <span>{fitContentLabel}</span>
        </button>

        <button
          type="button"
          className={[styles.ctrlBtn, styles.ctrlBtnLabeled].join(' ')}
          onClick={zoomFit}
          title="Fit timeline to view (show all)"
          aria-label="Fit timeline to view"
        >
          <i className="ph-bold ph-arrows-horizontal" />
          <span>Fit timeline</span>
        </button>

        <span className={styles.timecode} data-tc-anchor>
          {fmtTime(displayPlayheadSec)} / {fmtTime(totalDur)}
        </span>
        {mixerSlot && <div className={styles.mixerSlot}>{mixerSlot}</div>}
      </div>

      <TimelineMinimap
        chips={minimapChips}
        totalDur={totalDur}
        trackWidth={trackWidth}
        wrapW={wrapW}
        scrollLeft={scrollLeft}
        fitPxPerSec={fitPxPerSec}
        pxPerSecMax={PX_PER_SEC_MAX}
        scrollWrapRef={scrollWrapRef}
        setScrollLeft={setScrollLeft}
        setPxPerSecManual={setPxPerSecManual}
        handleDragActiveRef={handleDragActiveRef}
        activeVoiceIds={minimapActiveVoiceIds}
        assetVoiceIds={minimapAssetVoiceIds}
      />

      <div ref={scrollWrapRef} className={wrapCls}>
        {(() => {
          // Shared overlays (ruler/ticks/overflow-shade/end-marker/playhead)
          // span the full track height. In single-track mode they live
          // inside `.track`. In multi-track mode they live inside
          // `.trackArea` (which spans the full chip-stack but excludes the
          // sticky-left trackNumberPane). The chrome JSX is identical —
          // we just splice it into the right container.
          const chromeOverlays = (
            <>
              {inOvertime && !hideOverflowShade && (
                <div
                  className={styles.overflowShade}
                  style={{ left: `${boundary * pxPerSec}px`, right: 0 }}
                />
              )}
              {subTicks.map((t) => {
                const isMain = labels.includes(t);
                const cls = [styles.tick, isMain ? styles.tickMain : '']
                  .filter(Boolean)
                  .join(' ');
                return (
                  <div
                    key={`tick-${t}`}
                    className={cls}
                    style={{ left: `${t * pxPerSec}px` }}
                  />
                );
              })}
              <div className={styles.ruler}>
                {labels.map((t, i) => {
                  const edgeCls =
                    i === 0
                      ? styles.rulerLabelStart
                      : i === labels.length - 1
                        ? styles.rulerLabelEnd
                        : '';
                  return (
                    <span
                      key={`lbl-${t}`}
                      className={edgeCls}
                      style={{ left: `${t * pxPerSec}px` }}
                    >
                      {t}s
                    </span>
                  );
                })}
              </div>
            </>
          );
          const endMarkerAndPlayhead = (
            <>
              {inOvertime && (
                <div
                  className={styles.endMarker}
                  style={{ left: `${boundary * pxPerSec}px` }}
                />
              )}
              {limitSec != null && limitSec > 0 && (
                <div
                  className={styles.limitMarker}
                  style={{ left: `${limitSec * pxPerSec}px` }}
                  title={`Time limit ${limitSec}s`}
                >
                  <span className={styles.limitLabel}>
                    <i className="ph-bold ph-warning" aria-hidden="true" /> {limitSec}s
                  </span>
                </div>
              )}
              {/* Playhead — MUST be the LAST child of its track / trackArea
                  parent so its pointer-event capture order matches the
                  legacy implementation. */}
              <div
                className={[
                  styles.playhead,
                  isPlayheadDragging ? styles.playheadDragging : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ left: `${displayPlayheadSec * pxPerSec}px` }}
                onPointerDown={onPlayheadPointerDown}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.playheadHandle} />
              </div>
            </>
          );

          if (multiTracks && multiTracks.length >= 2) {
            // Multi-track render — N stacked voice rows + sticky-left
            // number pane. The trackRef points at .trackArea so ruler-
            // click / chip drag math (clientX - track.left) stays correct.
            // ruler-click is bound to .trackArea (NOT .trackStack) so
            // clicks on the sticky number pane don't accidentally seek
            // the playhead.
            //
            // Multi-Voice (#173): shrink each row by 20% per extra voice
            // (= 20% smaller for 2 voices, 40% smaller for 3). The chip
            // fills `row − 4` so the inter-chip vertical gap stays at
            // 4px regardless of row size; the ruler-clearance for the
            // top row comes from a 16px padding-top on .trackArea +
            // .trackNumberPane (so we don't have to leave a 20px gap
            // inside row 1 specifically).
            const BASE_TRACK = 76;
            const shrink =
              multiTracks.length === 3 ? 0.6
              : multiTracks.length === 2 ? 0.8
              : 1;
            const trackH = Math.round(BASE_TRACK * shrink);
            const chipH = Math.max(16, trackH - 4);
            const trackStackStyle = {
              ['--voice-track-h' as string]: `${trackH}px`,
              ['--voice-chip-h' as string]: `${chipH}px`,
            } as CSSProperties;
            return (
              <div className={styles.trackStack} style={trackStackStyle}>
                <div className={styles.trackNumberPane}>
                  {multiTracks.map((t) => (
                    <div
                      key={`num-${t.key}`}
                      className={styles.trackNumber}
                      style={{ color: t.numberColor }}
                      title={t.numberTitle}
                    >
                      {t.numberLabel}
                    </div>
                  ))}
                </div>
                <div
                  ref={trackRef}
                  className={styles.trackArea}
                  style={{ width: `${trackWidth}px` }}
                  onClick={handleRulerClick}
                >
                  {chromeOverlays}
                  {multiTracks.map((t) => (
                    <div
                      key={`row-${t.key}`}
                      className={styles.voiceTrack}
                      data-voice-id={t.key}
                    >
                      {t.trackChildren}
                    </div>
                  ))}
                  {/* Cross-track overlay (drag-popup etc.) spans full
                      height of .trackArea. Rendered BEFORE end-marker +
                      playhead so the playhead stays on top. */}
                  {children}
                  {endMarkerAndPlayhead}
                </div>
              </div>
            );
          }

          // Single-track render (default — STS + single-voice TTS).
          return (
            <div
              ref={trackRef}
              className={styles.track}
              style={{ width: `${trackWidth}px` }}
              onClick={handleRulerClick}
            >
              {chromeOverlays}
              {/* Per-flavour chip stack — chips, ghosts, drag-popup.
                  Rendered BEFORE the end-marker + playhead so chip
                  z-index 10 (during drag) lifts above the marker but
                  the static playhead at z-index 3 stays above non-
                  dragged chips. MUST be a flat list. */}
              {children}
              {endMarkerAndPlayhead}
            </div>
          );
        })()}
      </div>

      {overlaySlot}
    </div>
  );
}
